"use client"
import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Button from "../../components/common/Button"
import DashboardLayout from "../../components/layout/DashboardLayout"
import Alert from "../../components/common/alert"
import { getAllPackages, deletePackage } from "../../api/packageService"
import { ExternalLink, Plus, Search, ChevronLeft, ChevronRight, MoreHorizontal, Trash2, Edit, } from "lucide-react"

interface Package {
  _id: string
  title: string
  description: string
  bannerUrl: string
  themeUrl: string
  createdAt: string
  
  isDraft: boolean
  status: string
  isActive: boolean
  priceDetails: Array<{
    period: string
    actualPrice: number
    price: number
    gst: number
    totalprice: number
  }>
}

const PackageList: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [packages, setPackages] = useState<Package[]>([])
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState<{
    type: "success" | "error" | "info" | "warning"
    message: string
  } | null>(null)
  const [activeTab, setActiveTab] = useState<"all" | "Published" | "Draft" | "Sold out">("all")
  const [showSoldOutPopup, setShowSoldOutPopup] = useState(false)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await getAllPackages()

        if (response.status === 200 && response.data) {
          // Sort packages by creation date (latest first)
          const sortedPackages = [...response.data].sort((a, b) => 
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          setPackages(sortedPackages)
        } else {
          setAlert({
            type: "error",
            message: response.message || "Failed to load packages",
          })
        }
      } catch (error) {
        setAlert({
          type: "error",
          message: "Please check your authentication and try again",
        })
      } finally {
        setLoading(false)
      }
    }

    fetchPackages()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const handleDeletePackage = async (packageId: string, packageTitle: string) => {
    if (window.confirm(`Are you sure you want to delete the package "${packageTitle}"?`)) {
      try {
        const response = await deletePackage(packageId)
        if (response.status === 200) {
          // Remove the deleted package from the state
          setPackages(prevPackages => prevPackages.filter(pkg => pkg._id !== packageId))
          setAlert({
            type: "success",
            message: `Package "${packageTitle}" has been deleted successfully.`,
          })
        } else {
          setAlert({
            type: "error",
            message: response.message || "Failed to delete package",
          })
        }
      } catch (error) {
        setAlert({
          type: "error",
          message: "An error occurred while deleting the package",
        })
      }
    }
  }

  const handleViewPackage = (packageId: string, status: string) => {
    if (status === "Sold out") {
      setShowSoldOutPopup(true)
    } else {
      navigate(`/packages/${packageId}`)
    }
  }

const cleanDescription = (description: string = "") => {
  return description
    .replace(/\*\*(.*?)\*\*/g, "$1") // bold
    .replace(/__(.*?)__/g, "$1")     // bold
    .replace(/\*(.*?)\*/g, "$1")     // italic
    .replace(/_(.*?)_/g, "$1")       // italic
    .replace(/~~(.*?)~~/g, "$1")     // strikethrough
    .replace(/`(.*?)`/g, "$1")       // inline code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links
    .replace(/^\s*[-*+]\s+/gm, "")   // bullet points
    .replace(/^\s*\d+\.\s+/gm, "")   // numbered lists
    .trim()
}

  const filteredPackages = packages.filter((pkg) => {
    const matchesSearch = pkg.title.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTab = activeTab === "all" || pkg.status === activeTab
    return matchesSearch && matchesTab
  })

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentPackages = filteredPackages.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage)

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber)

  const pageNumbers = []
  const maxVisiblePages = 5

  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i)
  } else {
    pageNumbers.push(1)
    let startPage = Math.max(2, currentPage - 1)
    let endPage = Math.min(totalPages - 1, currentPage + 1)

    if (currentPage <= 2) endPage = 3
    if (currentPage >= totalPages - 1) startPage = totalPages - 2

    if (startPage > 2) pageNumbers.push('...')
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i)
    if (endPage < totalPages - 1) pageNumbers.push('...')
    pageNumbers.push(totalPages)
  }

  // Function to get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft": return "text-blue-600 bg-blue-50"
      case "Published": return "text-green-600 bg-green-50"
      case "Sold out": return "text-yellow-600 bg-yellow-50"
      case "Selling": return "text-purple-600 bg-purple-50"
      default: return "text-gray-600 bg-gray-50"
    }
  }

  if (loading) {
    return (
      <DashboardLayout >
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
          autoClose={true}
        />
      )}
      
      {showSoldOutPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Package Not Available</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  This package is sold out and is not available for viewing at this time.
                </p>
              </div>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowSoldOutPopup(false)}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3 sm:space-y-4">
        {/* Header Section */}
<div className="bg-white mt-3 rounded-md shadow-sm border border-gray-200 p-3 sm:p-4">
  <div className="flex items-center justify-between gap-3">
    <h2 className="text-lg sm:text-xl font-bold text-gray-900">
      📦 Package Management
    </h2>

    <Button
      onClick={() => navigate("/packages/create")}
      className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600
                 hover:from-purple-700 hover:via-purple-800 hover:to-blue-700
                 text-white inline-flex items-center justify-center
                 px-4 py-2 text-sm font-semibold rounded-md
                 transition-all duration-200 shadow-md hover:shadow-lg"
    >
      <Plus className="mr-1" size={14} />
      <span className="hidden sm:inline">Create</span>
      <span className="sm:hidden">Create</span>
    </Button>
  </div>

  {/* Search bar – full width */}
  <div className="mt-4">
    <div className="relative w-full">
      <Search className="absolute left-3 mt-3 text-gray-400" size={14} />
      <input
        id="search"
        type="text"
        placeholder="Search by package name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full pl-9 pr-3 py-2 text-sm
                   border border-gray-300 rounded-md
                   focus:outline-none focus:ring-1 focus:ring-purple-500
                   focus:border-transparent
                   bg-gray-50 hover:bg-white transition-all"
      />
    </div>
  </div>
</div>

        {/* Packages Table */}
        {filteredPackages.length > 0 ? (
          <div className="bg-white rounded-md shadow-md border border-gray-200 overflow-hidden">
            {/* Table Header with Status Tabs */}
            <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 px-3 py-3 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Your Packages</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    {filteredPackages.length} of {packages.length} packages shown
                  </p>
                </div>
                
                {/* Status Filter Tabs */}
                <div className="flex space-x-1 bg-white rounded-md p-1 border border-gray-200 shadow-sm">
                  <button
                    onClick={() => setActiveTab("all")}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all duration-200 ${
                      activeTab === "all"
                        ? "bg-purple-500 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setActiveTab("Published")}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all duration-200 ${
                      activeTab === "Published"
                        ? "bg-green-500 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    Published
                  </button>
                  <button
                    onClick={() => setActiveTab("Draft")}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all duration-200 ${
                      activeTab === "Draft"
                        ? "bg-blue-500 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    Draft
                  </button>
                  <button
                    onClick={() => setActiveTab("Sold out")}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all duration-200 ${
                      activeTab === "Sold out"
                        ? "bg-yellow-500 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    Sold Out
                  </button>
                </div>
              </div>
            </div>
            
            {/* Mobile Cards View */}
            <div className="block md:hidden">
              {currentPackages.map((pkg, index) => (
                <div key={pkg._id} className="border-b border-gray-100 p-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-blue-100 rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 font-bold text-xs">{pkg.title.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        onClick={() => handleViewPackage(pkg._id, pkg.status)}                        
                        className="font-semibold text-gray-900 hover:text-purple-600 transition-colors block text-xs cursor-pointer whitespace-normal break-words leading-5"
                      >
                        {pkg.title}
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2"> {cleanDescription(pkg.description)}</p>
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Created:</span>
                          <span className="text-gray-900 font-medium">
                            {new Date(pkg.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Status:</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${getStatusColor(pkg.status)}`}>
                            {pkg.status}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center space-x-2">
                        <button 
                          onClick={() => navigate(`/packages/edit/${pkg._id}`)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Edit Package"
                        >
                          <Edit size={12} />
                        </button>
                        <span className="text-gray-300 text-xs">•</span>
                        <button 
                          onClick={() => handleDeletePackage(pkg._id, pkg.title)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete Package"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-[600px]">
<table className="w-full table-fixed">
  <colgroup>
    <col className="w-[38%]" />
    <col className="w-[27%]" />
    <col className="w-[16%]" />
    <col className="w-[10%]" />
    <col className="w-[9%]" />
  </colgroup>

  <thead className="bg-gradient-to-r from-gray-50 via-purple-25 to-blue-25 border-b border-gray-200">
    <tr>
      <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
        Package Details
      </th>

      <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
        Description
      </th>

      <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
        Created Date
      </th>

      <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
        Status
      </th>

      <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
        Actions
      </th>
    </tr>
  </thead>

  <tbody className="divide-y divide-gray-100">
    {currentPackages.map((pkg, index) => (
      <tr
        key={pkg._id}
        className={`group hover:bg-gradient-to-r hover:from-purple-25 hover:via-blue-25 hover:to-indigo-25 transition-all duration-300 ${
          index % 2 === 0
            ? "bg-white"
            : "bg-gradient-to-r from-gray-25 to-purple-25"
        }`}
      >
        {/* PACKAGE DETAILS */}
<td className="py-3 px-3 align-top">
  <div
    onClick={() => handleViewPackage(pkg._id, pkg.status)}
    className="w-full flex items-start cursor-pointer text-gray-900 hover:text-purple-600 transition-all duration-200 group-hover:text-purple-700"
  >
    <div className="w-7 h-7 bg-gradient-to-br from-purple-100 to-blue-100 rounded-md flex items-center justify-center flex-shrink-0 mr-2">
      <span className="text-purple-600 font-bold text-xs">
        {pkg.title.charAt(0).toUpperCase()}
      </span>
    </div>

    <div className="flex-1 min-w-0">
      <div
        title={pkg.title}
        className="text-xs font-semibold leading-5"
        style={{
          whiteSpace: "normal",
          overflowWrap: "break-word",
          wordBreak: "normal",
        }}
      >
        {pkg.title}
      </div>

      <div className="flex items-center mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <ExternalLink className="text-purple-500" size={10} />
        <span className="ml-1 text-xs text-purple-600">
          View Details
        </span>
      </div>
    </div>
  </div>
</td>        

{/* DESCRIPTION */}
<td className="py-3 px-3 text-gray-600 text-xs align-top">
  <div
    className="w-full"
    title={cleanDescription(pkg.description)}
    style={{
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {cleanDescription(pkg.description)}
  </div>
</td>        

        {/* CREATED DATE */}
        <td className="py-3 px-3 text-gray-600 text-xs align-top">
          <div className="flex flex-col">
            <span className="font-medium">
              {new Date(pkg.createdAt).toLocaleDateString(
                "en-US",
                {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }
              )}
            </span>

            <span className="text-xs text-gray-400 mt-0.5">
              {new Date(pkg.createdAt).toLocaleDateString(
                "en-US",
                {
                  weekday: "long",
                }
              )}
            </span>
          </div>
        </td>

        {/* STATUS */}
        <td className="py-3 px-3 align-top">
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${getStatusColor(
              pkg.status
            )} whitespace-nowrap`}
          >
            {pkg.status}
          </span>
        </td>

        {/* ACTIONS */}
        <td className="py-3 px-3 align-top">
          <div className="flex space-x-1">
            <button
              onClick={() =>
                navigate(`/packages/edit/${pkg._id}`)
              }
              className="inline-flex items-center justify-center p-1.5 text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
              title="Edit Package"
            >
              <Edit size={12} />
            </button>

            <button
              onClick={() =>
                handleDeletePackage(pkg._id, pkg.title)
              }
              className="inline-flex items-center justify-center p-1.5 text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors"
              title="Delete Package"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </td>
      </tr>
    ))}
  </tbody>
</table>                
              </div>
            </div>
          </div>
        ) : null}

        {/* Empty State */}
        {!loading && filteredPackages.length === 0 && (
          <div className="text-center py-10">
            <div className="mx-auto w-14 h-14 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-7 h-7 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2m16-7H4m16 0l-2-9H6l-2 9" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">
              {searchTerm ? 'No packages found' : 'Create your first package'}
            </h3>
            <p className="text-gray-600 max-w-md mx-auto mb-5 text-xs leading-relaxed">
              {searchTerm 
                ? `No packages match "${searchTerm}". Try adjusting your search terms or create a new package.`
                : 'Start building your learning empire by creating your first educational package. It\'s quick and easy to get started.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-all duration-200"
                >
                  Clear Search
                </button>
              )}
              <Button
                onClick={() => navigate("/packages/create")}
                className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 hover:from-purple-700 hover:via-purple-800 hover:to-blue-700 text-white inline-flex items-center justify-center px-4 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <Plus className="mr-1" size={12} />
                Create Your First Package
              </Button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {filteredPackages.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-md border border-gray-200 px-3 py-2 shadow-sm">
            <div className="text-xs text-gray-700 mb-2 sm:mb-0">
              Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
              <span className="font-medium">{Math.min(indexOfLastItem, filteredPackages.length)}</span> of{" "}
              <span className="font-medium">{filteredPackages.length}</span> results
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`p-1 rounded border text-xs font-medium transition-all duration-200 ${
                  currentPage === 1 ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-white hover:bg-gray-50 hover:shadow-sm"
                }`}
              >
                <ChevronLeft size={12} />
              </button>

              {pageNumbers.map((pageNumber, index) => (
                <button
                  key={index}
                  onClick={() => typeof pageNumber === 'number' ? paginate(pageNumber) : null}
                  className={`px-2 py-1 rounded border text-xs font-medium min-w-[1.25rem] transition-all duration-200 ${
                    pageNumber === currentPage
                      ? "border-purple-500 bg-purple-100 text-purple-700 shadow-sm"
                      : pageNumber === '...'
                      ? "border-gray-300 bg-white text-gray-500 cursor-default"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:shadow-sm"
                  }`}
                  disabled={pageNumber === '...'}
                >
                  {pageNumber === '...' ? <MoreHorizontal size={12} /> : pageNumber}
                </button>
              ))}

              <button
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`p-1 rounded border text-xs font-medium transition-all duration-200 ${
                  currentPage === totalPages ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-white hover:bg-gray-50 hover:shadow-sm"
                }`}
              >
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}

export default PackageList